// btc15/liveRows.js — what each agent row and each open position says, once.
//
// The phone tab and the desktop tab both draw an agent list with a dot that
// flashes on a buy or a sell, and a list of open positions marked to the live
// book. The wording and the arithmetic live here so the two cannot drift: a
// position that reads +Ⓩ0.97 on the phone reads +Ⓩ0.97 on the desk.
import { markPrice, unrealised } from "./math";
import { agentDot } from "./pulse";

const cap = (side) => (side === "yes" ? "Up" : "Down");

export function buildLiveRows({ probs, wagers, yesBid, yesAsk, marketP, agreeMargin = 0.01, lastEvent, pulseState, nowMs,
  nameOf, labelOf, money, cents, pct, C }) {
  const openOf = (k) => wagers.filter((x) => x.agentKey === k && x.status === "open");
  const upnlOf = (list) => list.reduce((sum, x) => {
    const mk = markPrice(x, yesBid, yesAsk);
    return sum + Number((mk != null ? unrealised(x, mk) : x.mark?.pnl) || 0);
  }, 0);
  const tone = (v) => (Number(v) > 0 ? C.green : Number(v) < 0 ? C.red : C.muted);

  const stripRows = Object.entries(probs.calibrated || {}).map(([k, p]) => {
    const open = openOf(k);
    const dot = agentDot(lastEvent?.[k], open.length, pulseState, nowMs);
    const diff = marketP != null ? p - marketP : null;
    const clipped = p <= 0.02 + 1e-9 || p >= 0.98 - 1e-9;
    const none = diff == null || clipped || Math.abs(diff) <= agreeMargin + 1e-9;
    const ev = dot.event;
    let sub, subColor;
    if (dot.flashing) {
      sub = `${ev.kind === "buy" ? "Bought" : "Sold"} ${cap(ev.side)} @ ${cents(ev.price)}${ev.kind !== "buy" && ev.pnl != null ? ` · ${money(ev.pnl)}` : ""}`;
      subColor = ev.kind === "buy" ? C.green : (Number(ev.pnl) >= 0 ? C.green : C.red);
    } else if (open.length) {
      const sides = [...new Set(open.map((x) => x.side))];
      sub = `In ${sides.length === 1 ? cap(sides[0]) : "both"}${open.length > 1 ? ` · ${open.length} positions` : ""} · ${labelOf(k)}`;
      subColor = sides.length === 1 ? (sides[0] === "yes" ? C.green : C.red) : C.accent;
    } else {
      sub = none ? `Not in · ${labelOf(k)}` : `Backs ${diff > 0 ? "Up" : "Down"} by ${Math.abs(Math.round(diff * 100))}¢ · ${labelOf(k)}`;
      subColor = none ? C.faint : (diff > 0 ? C.green : C.red);
    }
    const u = open.length ? upnlOf(open) : null;
    return { key: k, name: nameOf(k), p: pct(p), pColor: p >= 0.5 ? C.green : C.red, sub, subColor, dot,
      upnlText: u == null ? null : money(u), upnlColor: u == null ? null : tone(u),
      vs: diff == null ? "" : `${diff >= 0 ? "+" : ""}${Math.round(diff * 100)} vs mkt` };
  });

  const positionRows = wagers.filter((x) => x.status === "open" || x.status === "resting").map((x) => {
    const mk = markPrice(x, yesBid, yesAsk);
    const u = x.status === "open" ? Number((mk != null ? unrealised(x, mk) : x.mark?.pnl) || 0) : null;
    return { id: x.lockId, name: nameOf(x.agentKey), side: x.side,
      sub: x.status === "resting" ? `bid resting @ ${cents(x.price)}` : `in @ ${cents(x.price)} · now ${mk != null ? cents(mk) : "—"} · ${x.strategy || ""}`.replace(/ · $/, ""),
      pnlText: u == null ? "resting" : money(u), pnlColor: u == null ? C.faint : tone(u) };
  });
  return { stripRows, positionRows, openCount: wagers.filter((x) => x.status === "open").length };
}
